import React from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'

const USERNAME_RE = /^[a-zA-Z0-9._-]+$/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function validateRow(r, { roleKeys, existing, seen }) {
  const errs = []
  const u = r.username
  if (!u) errs.push('Username required')
  else if (u.length < 3 || u.length > 64) errs.push('Username must be 3-64 characters')
  else if (!USERNAME_RE.test(u)) errs.push('Username has invalid characters')
  else if (existing.has(u.toLowerCase())) errs.push('Username already exists')
  else if (seen.has(u.toLowerCase())) errs.push('Duplicate username in file')
  if (!r.name) errs.push('Full name required')
  if (!r.email) errs.push('Email required')
  else if (!EMAIL_RE.test(r.email)) errs.push('Invalid email')
  if (!r.role) errs.push('Role required')
  else if (!roleKeys.has(r.role)) errs.push(`Unknown role "${r.role}"`)
  if (r.password && r.password.length < 8) errs.push('Password must be at least 8 characters')
  if (!r.password && !r.send_welcome_email) errs.push('Password required when welcome email is No')
  return errs
}

export default function UserBulkUploadModal({ roles, existingUsernames, onClose, onDone }) {
  const [rows, setRows] = React.useState([])
  const [fileName, setFileName] = React.useState('')
  const [error, setError] = React.useState(null)
  const [note, setNote] = React.useState(null)
  const [busy, setBusy] = React.useState(false)
  const [results, setResults] = React.useState(null) // { [row_index]: 'ok...' | error message }
  const [done, setDone] = React.useState(false)

  const roleKeys = React.useMemo(() => new Set((roles || []).map((r) => r.role)), [roles])
  const existing = React.useMemo(
    () => new Set((existingUsernames || []).map((u) => String(u).toLowerCase())),
    [existingUsernames]
  )

  const validated = React.useMemo(() => {
    const seen = new Set()
    return rows.map((r) => {
      const errs = validateRow(r, { roleKeys, existing, seen })
      if (r.username) seen.add(r.username.toLowerCase())
      return { ...r, errs }
    })
  }, [rows, roleKeys, existing])

  const isOk = (v) => String(v || '').startsWith('ok')
  const validRows = validated.filter(
    (r) => r.errs.length === 0 && !isOk(results?.[r.row_index])
  )

  const downloadTemplate = async () => {
    try {
      setError(null)
      const res = await authFetch(getLocalApiUrl('/api/admin/user-bulk/template'))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const url = URL.createObjectURL(await res.blob())
      const a = document.createElement('a')
      a.href = url
      a.download = 'user-bulk-upload-template.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e?.message || 'Failed to download template')
    }
  }

  const onFile = async (file) => {
    if (!file) return
    setBusy(true)
    setError(null)
    setNote(null)
    setResults(null)
    setDone(false)
    setFileName(file.name)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await authFetch(getLocalApiUrl('/api/admin/user-bulk/parse'), { method: 'POST', body: fd })
      const j = await res.json().catch(() => ({}))
      if (!res.ok || !j.success) throw new Error(j.error || `HTTP ${res.status}`)
      setRows(j.data.rows || [])
      if (j.data.truncated) setNote(`Only the first ${j.data.max_rows} rows were read. Upload the rest in a second file.`)
    } catch (e) {
      setRows([])
      setError(e?.message || 'Could not read file')
    } finally {
      setBusy(false)
    }
  }

  const createAll = async () => {
    if (!validRows.length) return
    if (!window.confirm(`Create ${validRows.length} user(s)?`)) return
    setBusy(true)
    setError(null)
    const out = { ...(results || {}) }
    for (const r of validRows) {
      try {
        const body = {
          username: r.username,
          name: r.name,
          email: r.email,
          role: r.role,
          active: r.active,
          send_welcome_email: r.send_welcome_email,
        }
        if (r.phone) body.phone = r.phone
        if (r.password) body.password = r.password
        const res = await authFetch(getLocalApiUrl('/api/admin/users'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        const j = await res.json().catch(() => ({}))
        if (!res.ok || !j.success) {
          out[r.row_index] = j.error || `HTTP ${res.status}`
        } else {
          const we = j.data?.welcomeEmail
          out[r.row_index] =
            r.send_welcome_email && we && !we.skipped && !we.success
              ? `ok (welcome email failed: ${we.error || 'unknown'})`
              : 'ok'
        }
      } catch (e) {
        out[r.row_index] = e?.message || 'Request failed'
      }
      setResults({ ...out })
    }
    setBusy(false)
    setDone(true)
    onDone?.()
  }

  const okCount = Object.values(results || {}).filter(isOk).length
  const failCount = Object.values(results || {}).length - okCount

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-white rounded shadow w-full max-w-4xl flex flex-col max-h-[calc(100vh-2rem)]" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between shrink-0">
          <div className="text-lg font-semibold">Bulk upload users</div>
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Close</button>
        </div>
        <div className="p-4 overflow-y-auto space-y-3">
          <p className="text-sm text-gray-600">
            Download the Excel template, fill in one user per row (up to 100), then upload it. Rows are checked first and nothing is created until you confirm.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button className="btn-secondary" onClick={downloadTemplate}>Download template</button>
            <input
              type="file"
              accept=".xlsx,.xls"
              disabled={busy}
              onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = '' }}
            />
            {fileName && <span className="text-xs text-gray-500">{fileName}</span>}
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
          {note && <div className="text-sm text-amber-700">{note}</div>}

          {validated.length > 0 && (
            <>
              <div className="text-sm">
                {validated.length} row(s): <span className="text-emerald-700">{validated.filter((r) => !r.errs.length).length} ready</span>,{' '}
                <span className="text-red-700">{validated.filter((r) => r.errs.length).length} with errors</span>
                {results && <> · created {okCount}{failCount > 0 && <>, failed {failCount}</>}</>}
              </div>
              <div className="overflow-x-auto border rounded">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left">Row</th>
                      <th className="px-2 py-1 text-left">Username</th>
                      <th className="px-2 py-1 text-left">Name</th>
                      <th className="px-2 py-1 text-left">Email</th>
                      <th className="px-2 py-1 text-left">Role</th>
                      <th className="px-2 py-1 text-left">Welcome email</th>
                      <th className="px-2 py-1 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {validated.map((r) => {
                      const res = results?.[r.row_index]
                      return (
                        <tr key={r.row_index} className={r.errs.length ? 'bg-red-50' : ''}>
                          <td className="px-2 py-1">{r.row_index}</td>
                          <td className="px-2 py-1 font-mono">{r.username}</td>
                          <td className="px-2 py-1">{r.name}</td>
                          <td className="px-2 py-1">{r.email}</td>
                          <td className="px-2 py-1">{r.role}</td>
                          <td className="px-2 py-1">{r.send_welcome_email ? 'Yes' : 'No'}</td>
                          <td className="px-2 py-1">
                            {res ? (
                              <span className={isOk(res) ? 'text-emerald-700' : 'text-red-700'}>
                                {isOk(res) ? `Created${res.slice(2)}` : res}
                              </span>
                            ) : r.errs.length ? (
                              <span className="text-red-700">{r.errs.join('; ')}</span>
                            ) : (
                              <span className="text-gray-500">Ready</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2 shrink-0">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>{done ? 'Done' : 'Cancel'}</button>
          <button className="btn-primary" onClick={createAll} disabled={busy || validRows.length === 0}>
            {busy ? 'Working…' : `Create ${validRows.length} user${validRows.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  )
}
